/*
 * @Author: cheng
 * @Date: 2018-12-21 11:30:04
 * @Last Modified by: cheng
 * @Last Modified time: 2019-03-28 11:46:1:538
 *
 */
import * as React from 'react';
import { API, REFERENCE_SUFFIX, ENTITY_RECOGNIZER } from '@config/index';
import { cloneDeep, result } from 'lodash';
import { CloseOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import { Input, InputNumber, Popover } from 'antd';
import { ReferenceConfig, ReferenceUpdateConfig } from './reference-textarea';
import { localizedString } from '@locale/index';
import {
  PositionConfig,
  ParameterType,
  ParameterConfig,
  SlotType,
  FrameType,
  DataQuery
} from '@interface/index';
import { ParameterMapStore } from '@lib/index';

const Search = Input.Search;

export type AutoCompleteMenuItem = {
  text: string;
  index: number;
};

export interface MenuConfig {
  position: PositionConfig;
  rangeX: number;
  rangeX2: number;
}

export interface AutoCompleteMenuPropsConfig {
  visible: boolean;
  trigger: string;
  textAreaRef?: HTMLTextAreaElement;
  menuConf: MenuConfig;
  customSuggestions?: string[];
  onlyUseCustomSuggestion?: boolean;
  update: (value: string, index: number) => void;
  close: () => void;
  onRef?: (ref: AutoCompleteMenu) => void;
}
export interface AutoCompleteMenuStateConfig {
  suggestions: string[];
  options: AutoCompleteMenuItem[];
  selectedIndex: number;
}

let defaultState: AutoCompleteMenuStateConfig = {
  suggestions: [],
  options: [],
  selectedIndex: 0
};

function generReg(val: string) {
  return new RegExp(`(.*)(${val.split('').join(')(.*)(')})(.*)`, 'i');
}

const defaultOptions = ['map()'];


function getParameterOptions(custom: string[], customOnly: boolean) {
  const set = new Set<string>();

  for (const c of custom) {
    set.add(c);
  }

  if (customOnly) {
    return Array.from(set);
  }


  const allValues = Object.values(ParameterMapStore.all());

  for (const v of allValues) {

    const nameComps = (v.name || '').split('.');
    let signature = nameComps[nameComps.length - 1] || '';

    if (v.type === ParameterType.CLOSURE) {
      signature += '(';

      const params: string[] = [];

      for (const p of v.parameters || []) {
        let pStr = p.name + ':';

        const typeComps = (p.type && p.type.name || '').split('.');
        const typeName = typeComps[typeComps.length - 1];

        pStr += p.multiValue
          ? `Array<${typeName}>`
          : typeName;
        params.push(pStr);
      }

      signature += params.join(',');
      signature += ')';
    }

    set.add(signature);
  }

  // default options
  for (const opt of defaultOptions) {
    set.add(opt);
  }

  return Array.from(set);
}

export class AutoCompleteMenu extends React.Component<
  AutoCompleteMenuPropsConfig,
  AutoCompleteMenuStateConfig
> {
  private $container?: HTMLDivElement;
  constructor(props: AutoCompleteMenuPropsConfig) {
    super(props);

    this.state = {
      ...defaultState,
      suggestions: getParameterOptions(props.customSuggestions || [], props.onlyUseCustomSuggestion || false)
    };
  }

  componentDidMount() {
    if (this.props.onRef) {
      this.props.onRef(this);
    }

    this.onInit();
  }

  componentDidUpdate(prevProps: AutoCompleteMenuPropsConfig) {

    if (prevProps.trigger !== this.props.trigger) {
      this.onInit();
    }
  }

  onInit = async () => {
    const { textAreaRef, trigger, close } = this.props;
    const { suggestions } = this.state;

    const options = suggestions
      .filter((sug: string) => {
        return generReg(trigger).test(sug);
      })
      .sort((a: string, b: string) => {
        return a.length < b.length ? -1 : 1;
      })
      .map((sug: string) => {

        return {
          text: sug,
          index: sug.length - trigger.length
        };
      });


    if (!options || !options.length) {
      close();
      return;
    }

    this.setState({
      options,
      selectedIndex: 0
      // if (textAreaRef) {
      //   textAreaRef.blur();
      // }

      // document.body.addEventListener('click', this.handleVisible, false);
    });

  }
  componentWillUnmount() {
    const { textAreaRef, menuConf } = this.props;
    const { selectedIndex, options } = this.state;

    document.body.removeEventListener('click', this.handleVisible, false);

    setTimeout(() => {

      // const index = menuConf.rangeX + (selectedIndex && options[selectedIndex].index || 0);

      if (textAreaRef) {
        textAreaRef.focus();
        // textAreaRef.setSelectionRange(index, index);
      }

      defaultState = {
        suggestions: [],
        options: [],
        selectedIndex: 0
      };
    });
  }

  render() {
    const { menuConf } = this.props;
    const {
      options,
      selectedIndex
    } = this.state;

    return (
      <div
        className="reference-menu__container"
        ref={this.containerRef}
        tabIndex={0}
        style={{
          position: 'absolute',
          left: menuConf.position.x,
          top: menuConf.position.y,
          maxWidth: 200
        }}
      >
        <div className="reference-menu__main">
          <ul className="reference-menu" key="menu">
            {options.map((opt: AutoCompleteMenuItem, index: number) => {

              const optionComp: any = opt.text;

              return <li
                className={`reference-menu__item ${index === selectedIndex ? 'reference-menu__item-selected' : ''}`}
                key={opt.text}
                onClick={(() => {
                  this.handleSelect(opt);
                }) || undefined}
                title={opt.text}
              >
                {optionComp}
              </li>;
            }
            )}
          </ul>
        </div>
      </div>
    );
  }


  containerRef = (ref: HTMLDivElement) => {
    this.$container = ref;
  };

  handleVisible = (e: any) => {
    if (!this.$container || !this.$container.contains(e.target)) {
      // this.props.referenceData.text = this.props.referenceData.text.substring(
      //   0,
      //   this.props.referenceData.text.length - 1
      // );
      // this.props.update({ nextReferenceData: this.props.referenceData });
      this.props.close();
    }
  };

  handleSelect = async (selected: AutoCompleteMenuItem) => {
    const { update, close } = this.props;

    // this.insertAtCursor(selected.text);
    update(selected.text, selected.index);
  };

  insertAtCursor = (value: string) => {
    const state = this.state;
    const props = this.props;
    const curValue = value;

    if (!props.textAreaRef) {
      return;
    }

    props.textAreaRef.value =
      curValue.substring(0, props.menuConf.rangeX) +
      value +
      curValue.substring(props.menuConf.rangeX2, curValue.length);

    props.textAreaRef.selectionStart = props.menuConf.rangeX + value.length;
    props.textAreaRef.selectionEnd = props.menuConf.rangeX2 + value.length;

    props.textAreaRef.dispatchEvent(new Event('input'));

    return props.textAreaRef.value;
  };

  public handleHotKey = (key: string) => {

    const { selectedIndex, options } = this.state;

    switch (key) {
      case 'tab':
      case 'return':
        this.handleSelect(options[selectedIndex || 0]);
        break;
      case 'down': {
        let nextIndex = selectedIndex + 1;

        if (nextIndex >= options.length) {
          nextIndex = 0;
        }

        this.setState({ selectedIndex: nextIndex });
      }
        break;
      case 'up': {
        let nextIndex = selectedIndex - 1;

        if (nextIndex < 0) {
          nextIndex = options.length - 1;
        }

        this.setState({ selectedIndex: nextIndex });
      }
        break;
      default:
        break;
    }
  }
}
